import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { authService, User } from '../services/GCPAuthService';
import { authConfig } from '@/shared/config/auth.config';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: () => void;
  loginWithCredentials: (
    username: string,
    password: string
  ) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  checkAuthStatus: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const initializeAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const storedToken = authService.getStoredToken();
      if (storedToken) {
        setUser(storedToken.user);
        setToken(storedToken.token);

        const currentUser = await authService.getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
        } else {
          setUser(null);
          setToken(null);
        }
      }
    } catch (err) {
      console.error('Error initializing auth:', err);
      setError('Failed to initialize authentication');
      setUser(null);
      setToken(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const setupTokenRefresh = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    if (authConfig.enableAutoRefresh && token) {
      refreshIntervalRef.current = setInterval(async () => {
        try {
          const storedToken = authService.getStoredToken();
          if (storedToken && authService.isTokenExpired()) {
            const newToken = await authService.refreshToken();
            if (newToken) {
              setToken(newToken.token);
              setUser(newToken.user);
            }
          }
        } catch (err) {
          console.error('Error refreshing token:', err);
        }
      }, authConfig.tokenRefreshInterval);
    }
  }, [token]);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  useEffect(() => {
    setupTokenRefresh();
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [setupTokenRefresh]);

  const login = useCallback(() => {
    setError(null);
    authService.initiateOIDCLogin();
  }, []);

  const loginWithCredentials = useCallback(
    async (username: string, password: string): Promise<boolean> => {
      try {
        setIsLoading(true);
        setError(null);

        const authToken = await authService.loginWithCredentials(
          username,
          password
        );
        if (authToken) {
          setUser(authToken.user);
          setToken(authToken.token);
          return true;
        } else {
          setError('Invalid username or password');
          return false;
        }
      } catch (err) {
        console.error('Error logging in:', err);
        setError('Failed to login');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      await authService.logout();
      setUser(null);
      setToken(null);

      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }

      window.location.href = authConfig.redirectPaths.afterLogout;
    } catch (err) {
      console.error('Error logging out:', err);
      setError('Failed to logout');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshToken = useCallback(async () => {
    try {
      setError(null);

      const newToken = await authService.refreshToken();
      if (newToken) {
        setUser(newToken.user);
        setToken(newToken.token);
      } else {
        setUser(null);
        setToken(null);
      }
    } catch (err) {
      console.error('Error refreshing token:', err);
      setError('Failed to refresh authentication');
      setUser(null);
      setToken(null);
    }
  }, []);

  const checkAuthStatus = useCallback(async () => {
    try {
      setError(null);

      const status = await authService.checkAuthStatus();
      if (status.authenticated && status.user) {
        setUser(status.user);
        const storedToken = authService.getStoredToken();
        if (storedToken) {
          setToken(storedToken.token);
        }
      } else {
        setUser(null);
        setToken(null);
      }
    } catch (err) {
      console.error('Error checking auth status:', err);
      setError('Failed to check authentication status');
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    error,
    login,
    loginWithCredentials,
    logout,
    refreshToken,
    checkAuthStatus,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
